import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaInstagram, FaFacebookF, FaTwitter, FaWhatsapp } from 'react-icons/fa';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <ContactContainer>
      <PageHeader>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Contact Us
        </motion.h1>
        <motion.div
          className="underline"
          initial={{ width: 0 }}
          animate={{ width: '80px' }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Have questions or ready to start your custom jewelry journey? Reach out to us today.
        </motion.p>
      </PageHeader>

      <ContactInfoSection>
        <ContactGrid>
          <ContactInfoColumn>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <ContactInfoTitle>Get In Touch</ContactInfoTitle>
              <ContactInfoText>
                We're here to answer any questions about our services or to help you start your custom jewelry journey.
              </ContactInfoText>

              <ContactInfoList>
                <ContactInfoItem>
                  <IconBox>
                    <FaMapMarkerAlt />
                  </IconBox>
                  <div>
                    <h4>Our Location</h4>
                    <p>6901 Security Blvd, Baltimore, MD 21244</p>
                  </div>
                </ContactInfoItem>

                <ContactInfoItem>
                  <IconBox>
                    <FaPhoneAlt />
                  </IconBox>
                  <div>
                    <h4>Phone Number</h4>
                    <p>(301) 538-8046</p>
                  </div>
                </ContactInfoItem>

                <ContactInfoItem>
                  <IconBox>
                    <FaEnvelope />
                  </IconBox>
                  <div>
                    <h4>Email Address</h4>
                    <p>baltimoregrillz@gmail.com</p>
                  </div>
                </ContactInfoItem>

                <ContactInfoItem>
                  <IconBox>
                    <FaClock />
                  </IconBox>
                  <div>
                    <h4>Business Hours</h4>
                    <p>Mon - Thu: 11AM - 7PM</p>
                    <p>Fri - Sat: 11AM - 8PM</p>
                    <p>Sunday: 12PM - 6PM</p>
                  </div>
                </ContactInfoItem>
              </ContactInfoList>

              <SocialLinks>
                <SocialLink href="https://www.instagram.com/bmoregrillz?igsh=MXNhb3dzNWVjeHNoMw==" target="_blank" rel="noopener noreferrer">
                  <FaInstagram />
                </SocialLink>
                <SocialLink href="#" target="_blank" rel="noopener noreferrer">
                  <FaFacebookF />
                </SocialLink>
                <SocialLink href="#" target="_blank" rel="noopener noreferrer">
                  <FaTwitter />
                </SocialLink>
              </SocialLinks>
            </motion.div>
          </ContactInfoColumn>

          <ContactFormColumn>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
              className="form-container"
            >
              <ContactFormTitle>Message Us on WhatsApp</ContactFormTitle>
              <WhatsAppDescription>
                Ready to start your custom jewelry journey? Click below to chat with us directly on WhatsApp!
              </WhatsAppDescription>
              <WhatsAppButton
                href="https://wa.me/13015388046"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -5, boxShadow: '0 15px 35px rgba(37, 211, 102, 0.4)' }}
                whileTap={{ scale: 0.95 }}
              >
                <FaWhatsapp />
                Chat on WhatsApp
              </WhatsAppButton>
              <WhatsAppNote>
                We typically respond within a few hours during business hours.
              </WhatsAppNote>
            </motion.div>
          </ContactFormColumn>
        </ContactGrid>
      </ContactInfoSection>

      <MapSection>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>Visit Our Store</SectionTitle>
        </motion.div>

        <MapContainer>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3087.3!2d-76.7350!3d39.3150!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c81be46b5d8e8b%3A0x0!2s6901%20Security%20Blvd%2C%20Baltimore%2C%20MD%2021244!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="Baltimore Grillz & Custom Jewelry Location"
          ></iframe>
        </MapContainer>
      </MapSection>

      <FaqSection>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeIn}
        >
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <FaqDescription>
            Answers to some common questions about our services and processes.
          </FaqDescription>
        </motion.div>

        <FaqGrid>
          <FaqItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <FaqQuestion>How long does it take to create a custom piece?</FaqQuestion>
            <FaqAnswer>
              The timeline for custom pieces varies depending on complexity, materials, and current workload. Generally, custom grillz take 2-3 weeks, while more complex jewelry pieces may take 3-6 weeks from consultation to completion.
            </FaqAnswer>
          </FaqItem>

          <FaqItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <FaqQuestion>Do you offer payment plans?</FaqQuestion>
            <FaqAnswer>
              Yes, we offer flexible payment plans for custom pieces. Typically, we require a 50% deposit to begin work, with the remaining balance due upon completion. For larger projects, we can arrange more flexible payment schedules.
            </FaqAnswer>
          </FaqItem>

          <FaqItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaqQuestion>How do you ensure the quality of your materials?</FaqQuestion>
            <FaqAnswer>
              We source all our materials from reputable suppliers and ensure they meet our high standards. Our gold, diamonds, and gemstones come with proper certification, and we perform in-house testing to verify their authenticity and quality.
            </FaqAnswer>
          </FaqItem>

          <FaqItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <FaqQuestion>Do you provide warranties or guarantees?</FaqQuestion>
            <FaqAnswer>
              Yes, all our custom pieces come with a craftsmanship warranty. We stand behind the quality of our work and will address any issues related to craftsmanship at no additional cost within the warranty period.
            </FaqAnswer>
          </FaqItem>

          <FaqItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <FaqQuestion>Can you work with my old jewelry to create something new?</FaqQuestion>
            <FaqAnswer>
              Absolutely! We specialize in transforming old or unworn jewelry into new, personalized pieces. We can repurpose gemstones, melt down precious metals, and incorporate these elements into fresh, contemporary designs.
            </FaqAnswer>
          </FaqItem>

          <FaqItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <FaqQuestion>Do I need an appointment to visit your store?</FaqQuestion>
            <FaqAnswer>
              While walk-ins are welcome during regular business hours, we recommend scheduling an appointment for consultations on custom pieces. This ensures that you receive dedicated attention from our design team and allows us to prepare for your visit.
            </FaqAnswer>
          </FaqItem>
        </FaqGrid>
      </FaqSection>
    </ContactContainer>
  );
};

// Styled Components
const ContactContainer = styled.div`
  background-color: var(--dark-bg);
  color: var(--white);
`;

const PageHeader = styled.div`
  padding: 150px 0 80px;
  text-align: center;
  background: linear-gradient(to bottom, var(--darker-bg), var(--dark-bg));
  position: relative;
  
  h1 {
    font-size: 4rem;
    text-transform: uppercase;
    font-weight: 800;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
    
    @media (max-width: 576px) {
      font-size: 2.5rem;
    }
  }
  
  .underline {
    height: 4px;
    background: var(--gold-gradient);
    margin: 0 auto;
    border-radius: 2px;
  }
  
  p {
    max-width: 800px;
    margin: var(--spacing-md) auto 0;
    font-size: 1.1rem;
    color: var(--silver);
    
    @media (max-width: 768px) {
      font-size: 1rem;
      padding: 0 var(--spacing-md);
    }
  }
`;

const ContactInfoSection = styled.section`
  padding: var(--spacing-xl) 0;
  
  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const ContactInfoColumn = styled.div`
  padding: var(--spacing-lg);
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  box-shadow: var(--shadow-lg);
  
  @media (max-width: 576px) {
    padding: var(--spacing-md);
  }
`;

const ContactFormColumn = styled.div`
  .form-container {
    padding: var(--spacing-lg);
    background: rgba(255, 255, 255, 0.02);
    border-radius: var(--radius-lg);
    border: 1px solid rgba(var(--gold-rgb), 0.1);
    box-shadow: var(--shadow-lg);
    
    @media (max-width: 576px) {
      padding: var(--spacing-md);
    }
  }
`;

const ContactInfoTitle = styled.h2`
  font-size: 2rem;
  color: white;
  margin-bottom: var(--spacing-md);
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: var(--gold-gradient);
    border-radius: 2px;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const ContactInfoText = styled.p`
  font-size: 1rem;
  color: #d0d0d0;
  margin-bottom: var(--spacing-lg);
  line-height: 1.6;
`;

const ContactInfoList = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h4 {
    font-size: 1.1rem;
    margin-bottom: 5px;
    color: white;
  }
  
  p {
    color: var(--silver);
    font-size: 0.95rem;
    margin: 0;
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-md);
  flex-shrink: 0;
  
  svg {
    font-size: 1.3rem;
    color: var(--gold);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
`;

const SocialLink = styled.a`
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
  
  svg {
    font-size: 1.2rem;
    color: var(--gold);
    transition: var(--transition-fast);
  }
  
  &:hover {
    background: var(--gold-gradient);
    border-color: transparent;
    
    svg {
      color: var(--black);
    }
  }
`;

const ContactFormTitle = styled.h2`
  font-size: 2rem;
  color: white;
  margin-bottom: var(--spacing-md);
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: var(--gold-gradient);
    border-radius: 2px;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const WhatsAppDescription = styled.p`
  font-size: 1.1rem;
  color: #d0d0d0;
  margin-bottom: var(--spacing-lg);
  line-height: 1.7;
  margin-top: var(--spacing-md);
`;

const WhatsAppButton = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 18px 32px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-medium);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.3);
  
  svg {
    font-size: 1.5rem;
  }
  
  &:hover {
    transform: translateY(-3px);
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    padding: 16px 24px;
  }
`;

const WhatsAppNote = styled.p`
  font-size: 0.9rem;
  color: #888;
  text-align: center;
  margin-top: var(--spacing-md);
  font-style: italic;
`;

const ContactForm = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-sm);
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  margin-bottom: 8px;
  color: var(--silver);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 15px;
  border-radius: var(--radius-md);
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  color: white;
  font-size: 1rem;
  transition: var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--gold);
    box-shadow: 0 0 0 2px rgba(var(--gold-rgb), 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 15px;
  border-radius: var(--radius-md);
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  color: white;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: var(--transition-fast);
  
  &:focus {
    outline: none;
    border-color: var(--gold);
    box-shadow: 0 0 0 2px rgba(var(--gold-rgb), 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 16px;
  background: var(--gold-gradient);
  color: var(--black);
  border: none;
  border-radius: var(--radius-md);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-medium);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: var(--spacing-sm);
  
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMessage = styled(motion.div)`
  text-align: center;
  padding: var(--spacing-lg);
  
  h3 {
    font-size: 2rem;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: var(--spacing-md);
  }
  
  p {
    font-size: 1.1rem;
    color: white;
    line-height: 1.6;
  }
`;

const MapSection = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  text-align: center;
  margin-bottom: var(--spacing-lg);
  position: relative;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 700;
  background: var(--gold-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const MapContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  
  iframe {
    display: block;
  }
`;

const FaqSection = styled.section`
  padding: var(--spacing-xl) 0;
  
  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const FaqDescription = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: var(--silver);
  margin-bottom: var(--spacing-xl);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: var(--spacing-lg);
    padding: 0 var(--spacing-md);
  }
`;

const FaqGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(500px, 1fr));
  gap: var(--spacing-lg);
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FaqItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-md);
  padding: var(--spacing-lg);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  transition: var(--transition-medium);
  
  &:hover {
    border-color: rgba(var(--gold-rgb), 0.3);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 576px) {
    padding: var(--spacing-md);
  }
`;

const FaqQuestion = styled.h3`
  font-size: 1.3rem;
  color: var(--gold);
  margin-bottom: var(--spacing-sm);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: var(--gold-gradient);
  }
  
  @media (max-width: 576px) {
    font-size: 1.1rem;
  }
`;

const FaqAnswer = styled.p`
  font-size: 1rem;
  color: var(--silver);
  line-height: 1.6;
  
  @media (max-width: 576px) {
    font-size: 0.95rem;
  }
`;

export default Contact; 